import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Display {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  holiday_type: string;
  display_type: string;
  image_url: string;
  featured: boolean;
  upvote_count: number;
}

interface SimpleMapViewProps {
  displays: Display[];
  userLocation?: { latitude: number; longitude: number } | null;
  onDisplayClick?: (display: Display) => void;
  className?: string;
}

export const SimpleMapView: React.FC<SimpleMapViewProps> = ({
  displays,
  userLocation,
  onDisplayClick,
  className = ""
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Set Mapbox access token
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: userLocation ? [userLocation.longitude, userLocation.latitude] : [-98.5795, 39.8283], // Center of US
      zoom: userLocation ? 10 : 4,
      maxZoom: 18,
      minZoom: 2
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add user location control
    map.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      }),
      'top-right'
    );

    // Wait for map to load
    map.current.on('load', () => {
      setMapLoaded(true);
    });

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [userLocation]);

  // Update displays when data changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !displays.length) return;

    // Convert displays to GeoJSON
    const geojsonData = {
      type: 'FeatureCollection' as const,
      features: displays.map(display => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [display.longitude, display.latitude]
        },
        properties: {
          id: display.id,
          name: display.name,
          description: display.description,
          city: display.city,
          state: display.state,
          holiday_type: display.holiday_type,
          display_type: display.display_type,
          image_url: display.image_url,
          featured: display.featured,
          upvote_count: display.upvote_count
        }
      }))
    };

    // Remove existing source and layers if they exist
    if (map.current.getSource('displays')) {
      if (map.current.getLayer('clusters')) map.current.removeLayer('clusters');
      if (map.current.getLayer('cluster-count')) map.current.removeLayer('cluster-count');
      if (map.current.getLayer('unclustered-point')) map.current.removeLayer('unclustered-point');
      map.current.removeSource('displays');
    }

    // Add displays source with clustering
    map.current.addSource('displays', {
      type: 'geojson',
      data: geojsonData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    });

    // Add cluster circles
    map.current.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'displays',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#3b82f6', // Blue for clusters with < 10 points (better contrast on dark)
          10,
          '#f59e0b', // Amber for clusters with 10-30 points
          30,
          '#ef4444'  // Red for clusters with > 30 points
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20, // Radius for clusters with < 10 points
          10,
          30, // Radius for clusters with 10-30 points
          30,
          40  // Radius for clusters with > 30 points
        ]
      }
    });

    // Add cluster count labels
    map.current.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'displays',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff'
      }
    });

    // Add unclustered points
    map.current.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'displays',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['get', 'featured'],
          '#fbbf24', // Bright gold for featured displays (better on dark)
          '#60a5fa'  // Lighter blue for regular displays (better on dark)
        ],
        'circle-radius': [
          'case',
          ['get', 'featured'],
          8, // Larger for featured displays
          6  // Regular size
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    });

    // Handle cluster clicks (zoom to cluster)
    map.current.on('click', 'clusters', (e) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });

      const clusterId = features[0].properties!.cluster_id;
      (map.current!.getSource('displays') as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;

          map.current!.easeTo({
            center: (features[0].geometry as GeoJSON.Point).coordinates as [number, number],
            zoom: zoom
          });
        }
      );
    });

    // Handle unclustered point clicks
    map.current.on('click', 'unclustered-point', (e) => {
      const features = e.features;
      if (!features || features.length === 0) return;

      const properties = features[0].properties;
      const display = displays.find(d => d.id === properties!.id);

      if (display && onDisplayClick) {
        onDisplayClick(display);
      }

      // Create popup
      const popup = new mapboxgl.Popup()
        .setLngLat((features[0].geometry as GeoJSON.Point).coordinates as [number, number])
        .setHTML(`
          <div style="padding: 8px; max-width: 200px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">${properties!.name}</h3>
            ${properties!.image_url ? `<img src="${properties!.image_url}" alt="${properties!.name}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;" />` : ''}
            <p style="margin: 0 0 4px 0; font-size: 12px; color: #666;">${properties!.city}, ${properties!.state}</p>
            ${properties!.holiday_type ? `<p style="margin: 0 0 4px 0; font-size: 11px; background: #e8f4fd; padding: 2px 6px; border-radius: 4px; display: inline-block;">${properties!.holiday_type}</p>` : ''}
            ${properties!.description ? `<p style="margin: 4px 0 0 0; font-size: 11px; color: #666; line-height: 1.3;">${properties!.description.substring(0, 100)}${properties!.description.length > 100 ? '...' : ''}</p>` : ''}
          </div>
        `)
        .addTo(map.current!);
    });

    // Change cursor on hover
    map.current.on('mouseenter', 'clusters', () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'clusters', () => {
      map.current!.getCanvas().style.cursor = '';
    });

    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current!.getCanvas().style.cursor = '';
    });

  }, [displays, mapLoaded, onDisplayClick]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <div ref={mapContainer} className="w-full h-full" />
      {!mapLoaded && (
        <div className="absolute inset-0 bg-muted flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};